import type { ICard } from "@/modules/card/common/types";
import { Amount } from "@/shared/components";
import { CollapseItem } from "@ab.uitools/ui-library/components/Collapse";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { type RefObject, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  card: ICard;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const CardDetailRows = ({ card, scrollbarContainerRef }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleCollapse = () => setIsOpen(!isOpen);


  return (
    <div className="pt-40">
      <div className="mb-16">
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("cards.accountBalance")}
            </Text>
            <Amount
              currency={card.account.currency}
              amount={card.account.balance || 0}
            />
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("cards.currentBalance")}
            </Text>
            <Amount
              currency={card?.currency}
              amount={card.account.expectedBalance || 0}
            />
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
          <Text type="secondary" size="medium" weight="semibold">
            {t("cards.accountNumber")}
          </Text>
          <div className="flexbox align-items--center">
            <Copy
              textAfterCopy={t("general.copied")}
              size="medium"
              parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
              text={card?.account?.number as string}
            />
            <Text size="medium" weight="bold">{card?.account?.number}</Text>
          </div>
        </div>
        <Divider
          color="light"
          type="primary"
          isHorizontal
        />
      </div>
      <CollapseItem
        className="detail-collapse"
        isOpen={isOpen}
        toggle={toggleCollapse}
        title={t("cards.additionalDetails")}
      >
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("cards.cardHolderName")}
            </Text>
            <Text size="medium" weight="bold">{card?.holder}</Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("cards.cardType")}
            </Text>
            <Text size="medium" weight="bold">{card?.description}</Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("cards.creationDate")}
            </Text>
            <Text size="medium" weight="bold">{card?.creationDate}</Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
        <div>
          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
            <Text type="secondary" size="medium" weight="semibold">
              {t("cards.activeUntil")}
            </Text>
            <Text size="medium" weight="bold">{card?.expirationDate}</Text>
          </div>
        </div>
      </CollapseItem>
      <Divider
        color="light"
        type="primary"
        isHorizontal
      />
    </div>
  );
};
